/**
 * The shipping companies the POS can hand a delivery order to, as the
 * frontends know them.
 *
 * MIRROR of `pos-api/src/integrations/shipping/registry.ts` — pos-api does
 * not depend on this package, so the two tables are kept identical by hand
 * and pinned by `registry.test.ts` there. Only what a screen needs lives
 * here: the code, the names, and whether the courier is driven by an API or
 * by hand.
 */

export type ShippingProviderCode = 'armada' | 'mashkor' | 'aramex' | 'wiyak' | 'paydo' | 'manual';

export interface ShippingProviderInfo {
  code: ShippingProviderCode;
  nameEn: string;
  nameAr: string;
  /** No API: the tracking reference is typed in and the status moved by hand. */
  manual: boolean;
  /** Integrated against published documentation. */
  contractVerified: boolean;
}

export const SHIPPING_PROVIDERS: Record<ShippingProviderCode, ShippingProviderInfo> = {
  armada: { code: 'armada', nameEn: 'Armada', nameAr: 'أرمادا', manual: false, contractVerified: true },
  mashkor: { code: 'mashkor', nameEn: 'Mashkor', nameAr: 'مشكور', manual: false, contractVerified: true },
  aramex: { code: 'aramex', nameEn: 'Aramex', nameAr: 'أرامكس', manual: false, contractVerified: true },
  wiyak: { code: 'wiyak', nameEn: 'Wiyak', nameAr: 'وياك', manual: true, contractVerified: false },
  paydo: { code: 'paydo', nameEn: 'Paydo', nameAr: 'بيدو', manual: true, contractVerified: false },
  manual: { code: 'manual', nameEn: 'Other courier (manual)', nameAr: 'شركة شحن أخرى (يدوي)', manual: true, contractVerified: true },
};

export const SHIPPING_PROVIDER_CODES: readonly ShippingProviderCode[] = ['armada', 'mashkor', 'aramex', 'wiyak', 'paydo', 'manual'];

export function isShippingProviderCode(value: unknown): value is ShippingProviderCode {
  return typeof value === 'string' && (SHIPPING_PROVIDER_CODES as readonly string[]).includes(value);
}

/**
 * The normalised shipment status model — the same ten words for every
 * courier. Mirror of `SHIPMENT_STATUSES` in shipping.types.ts.
 */
export type ShipmentStatus =
  | 'pending'
  | 'created'
  | 'picked_up'
  | 'in_transit'
  | 'out_for_delivery'
  | 'delivered'
  | 'failed_delivery'
  | 'returned'
  | 'cancelled'
  | 'exception';

export const SHIPMENT_STATUSES: readonly ShipmentStatus[] = [
  'pending',
  'created',
  'picked_up',
  'in_transit',
  'out_for_delivery',
  'delivered',
  'failed_delivery',
  'returned',
  'cancelled',
  'exception',
];

export const SHIPMENT_STATUS_LABELS: Record<ShipmentStatus, { en: string; ar: string }> = {
  pending: { en: 'Courier selected', ar: 'تم اختيار شركة الشحن' },
  created: { en: 'Shipment created', ar: 'تم إنشاء الشحنة' },
  picked_up: { en: 'Picked up', ar: 'تم الاستلام' },
  in_transit: { en: 'In transit', ar: 'في الطريق' },
  out_for_delivery: { en: 'Out for delivery', ar: 'خرجت للتوصيل' },
  delivered: { en: 'Delivered', ar: 'تم التوصيل' },
  failed_delivery: { en: 'Failed delivery', ar: 'فشل التوصيل' },
  returned: { en: 'Returned', ar: 'مرتجعة' },
  cancelled: { en: 'Cancelled', ar: 'ملغاة' },
  exception: { en: 'Needs attention', ar: 'تحتاج متابعة' },
};

/** Nothing moves after these. */
export const SHIPMENT_TERMINAL_STATUSES: ReadonlySet<ShipmentStatus> = new Set(['delivered', 'returned', 'cancelled']);

export interface ShipmentCapabilities {
  create: boolean;
  track: boolean;
  cancel: boolean;
  quote: boolean;
  label: boolean;
  webhook: boolean;
  manual: boolean;
}

/** What `GET /api/v1/shipping/providers` returns per activated courier. */
export interface ShippingProviderOption {
  code: ShippingProviderCode;
  name: string;
  name_ar: string;
  is_live: boolean;
  cod_enabled: boolean;
  customer_charge_mode: 'order' | 'provider' | 'fixed' | 'free';
  customer_charge_fixed: number;
  capabilities: ShipmentCapabilities;
  contract_verified: boolean;
  service_area_ids: number[];
}

/** A shipment as every screen sees it (`shipments.service.toView`). */
export interface ShipmentView {
  id: number;
  subsidiary_id: number;
  branch_id: number;
  receipt_id: number;
  provider_config_id: number;
  provider_code: ShippingProviderCode;
  provider_name: string;
  provider_name_ar: string;
  is_current: boolean;
  status: ShipmentStatus;
  provider_status: string | null;
  reference_no: string;
  provider_shipment_id: string | null;
  tracking_number: string | null;
  tracking_url: string | null;
  label_url: string | null;
  has_label_document: boolean;
  label_content_type: string | null;
  currency: string | null;
  customer_charge: number;
  actual_cost: number | null;
  quoted_cost: number | null;
  is_cod: boolean;
  cod_amount: number | null;
  customer_name: string | null;
  customer_phone: string | null;
  address: unknown;
  notes: string | null;
  driver_name: string | null;
  driver_phone: string | null;
  driver_latitude: number | null;
  driver_longitude: number | null;
  scheduled_at: string | null;
  estimated_delivery_at: string | null;
  provider_created_at: string | null;
  last_status_at: string | null;
  last_polled_at: string | null;
  delivered_at: string | null;
  cancelled_at: string | null;
  last_error: string | null;
  created_by: number | null;
  created_at: string;
  updated_at: string;
  capabilities: ShipmentCapabilities;
  can: {
    change_provider: boolean;
    quote: boolean;
    create: boolean;
    refresh: boolean;
    cancel: boolean;
    label: boolean;
    manual: boolean;
  };
}

export interface ShipmentEvent {
  id: number;
  shipment_id: number;
  status: ShipmentStatus;
  provider_status: string | null;
  description: string | null;
  location: string | null;
  source: 'create' | 'poll' | 'webhook' | 'manual' | 'cancel' | 'system';
  provider_event_id: string | null;
  event_at: string;
  created_by: number | null;
  created_at: string;
}
